import type { PublicWhoPlays, RegistrationFieldSection, RegistrationFormField, RegistrationFormSchema, PublicRegistrationSubmitInput, TournamentParticipant } from "./api";
import { registrationDocumentReceived } from "./registrationDocumentLinks";

export const SYSTEM_REQUIRED_REGISTRATION_FIELD_KEYS = new Set(["first_name", "last_name", "email"]);

export const DEFAULT_REGISTRATION_FORM_FIELDS: RegistrationFormField[] = [
  { key: "first_name", kind: "core", type: "text", label: "Nombre", required: true, enabled: true, locked: true, show_in_players_table: true, order: 10 },
  { key: "last_name", kind: "core", type: "text", label: "Apellido paterno", required: true, enabled: true, locked: true, show_in_players_table: true, order: 20 },
  { key: "maternal_last_name", kind: "core", type: "text", label: "Apellido materno", required: false, enabled: true, show_in_players_table: false, order: 30 },
  { key: "phone", kind: "core", type: "phone", label: "Celular", required: true, enabled: true, show_in_players_table: true, order: 40 },
  { key: "email", kind: "core", type: "email", label: "Correo", required: true, enabled: true, locked: true, show_in_players_table: true, order: 50 },
  { key: "birthdate", kind: "core", type: "date", label: "Fecha de nacimiento", required: true, enabled: true, show_in_players_table: true, order: 60 },
  { key: "gender", kind: "core", type: "select", label: "Género", required: true, enabled: true, show_in_players_table: true, options: ["male", "female"], order: 70 },
  { key: "nationality_code", kind: "core", type: "text", label: "Nacionalidad", required: true, enabled: true, show_in_players_table: false, order: 80 },
  { key: "state_province", kind: "core", type: "text", label: "Estado", required: true, enabled: true, show_in_players_table: false, order: 90 },
  { key: "city", kind: "core", type: "text", label: "Ciudad", required: true, enabled: true, show_in_players_table: false, order: 100 },
  { key: "club_name", kind: "core", type: "text", label: "Club / Academia", required: true, enabled: true, show_in_players_table: true, order: 110 },
  { key: "national_federation_id", kind: "core", type: "text", label: "ID Federativo / FMT", required: false, enabled: true, show_in_players_table: true, order: 120 },
  { key: "wtn_tennis_id", kind: "core", type: "text", label: "WTN Tennis ID", required: false, enabled: true, show_in_players_table: false, order: 130 },
  { key: "dominant_hand", kind: "core", type: "select", label: "Mano dominante", required: true, enabled: true, show_in_players_table: false, options: ["right", "left"], order: 140 },
  { key: "backhand_type", kind: "core", type: "select", label: "Tipo de revés", required: true, enabled: true, show_in_players_table: false, options: ["one_hand", "two_hands"], order: 150 },
  { key: "guardian_name", kind: "core", type: "text", label: "Nombre del tutor", required: true, conditional_required: "minor", enabled: true, show_in_players_table: false, order: 160 },
  { key: "guardian_phone", kind: "core", type: "phone", label: "Teléfono del tutor", required: true, conditional_required: "minor", enabled: true, show_in_players_table: false, order: 170 },
  { key: "guardian_relationship", kind: "core", type: "text", label: "Parentesco del tutor", required: true, conditional_required: "minor", enabled: true, show_in_players_table: false, order: 180 },
  { key: "guardian_email", kind: "core", type: "email", label: "Email del tutor", required: true, conditional_required: "minor", enabled: true, show_in_players_table: false, order: 190 },
  { key: "coach_name", kind: "core", type: "text", label: "Nombre del profesor", required: false, enabled: true, show_in_players_table: false, order: 200 },
  { key: "coach_phone", kind: "core", type: "phone", label: "Teléfono del profesor", required: false, enabled: true, show_in_players_table: false, order: 210 },
  { key: "coach_email", kind: "core", type: "email", label: "Email del profesor", required: false, enabled: true, show_in_players_table: false, order: 220 },
  { key: "curp_pdf", kind: "document", type: "file", label: "CURP en PDF", required: true, enabled: true, show_in_players_table: false, order: 300 },
  { key: "photo", kind: "document", type: "file", label: "Foto de medio cuerpo", required: true, enabled: true, show_in_players_table: false, order: 310 },
  { key: "payment_receipt", kind: "document", type: "file", label: "Comprobante de pago", required: false, enabled: true, show_in_players_table: true, order: 320 },
];

const MEX_TOUR_CUP_CUSTOM_FIELDS: RegistrationFormField[] = [
  {
    key: "mex_category_requested",
    kind: "custom",
    type: "select",
    label: "Categoría en la que deseas jugar",
    required: true,
    enabled: true,
    show_in_players_table: true,
    options: ["A", "B", "C", "D", "A-B Femenil", "Femenil C", "Femenil D", "Novatos"],
    help_text: "Esta es tu categoría solicitada; el organizador podrá validarla antes de armar el torneo.",
    order: 400,
  },
  {
    key: "mex_friday_availability",
    kind: "custom",
    type: "select",
    label: "¿Puedes jugar en viernes? Si sí, ¿a partir de qué horario?",
    required: true,
    enabled: true,
    show_in_players_table: true,
    options: ["No puedo viernes", "A partir de las 12:00 pm", "A partir de las 2:00 pm", "A partir de las 4:00 pm", "A partir de las 6:00 pm", "A partir de las 8:00 pm"],
    order: 410,
  },
  {
    key: "mex_saturday_availability",
    kind: "custom",
    type: "select",
    label: "Disponibilidad para jugar sábado",
    required: true,
    enabled: true,
    show_in_players_table: true,
    options: ["Horario libre", "A partir de la 1:00 pm", "A partir de las 2:00 pm", "A partir de las 3:00 pm", "A partir de las 4:00 pm", "A partir de las 5:00 pm", "A partir de las 6:00 pm", "No puedo sábado"],
    order: 420,
  },
  {
    key: "mex_sunday_availability",
    kind: "custom",
    type: "select",
    label: "Disponibilidad para jugar domingo",
    required: true,
    enabled: true,
    show_in_players_table: true,
    options: ["Horario libre", "A partir de la 1:00 pm", "A partir de las 2:00 pm", "A partir de las 3:00 pm", "A partir de las 4:00 pm", "A partir de las 5:00 pm", "A partir de las 6:00 pm", "No puedo domingo"],
    order: 430,
  },
  {
    key: "mex_consolation_preference",
    kind: "custom",
    type: "select",
    label: "Si quedas eliminado, ¿qué prefieres jugar como formato adicional?",
    required: true,
    enabled: true,
    show_in_players_table: true,
    options: ["Backdraw", "Dobles"],
    order: 440,
  },
  {
    key: "mex_utr_rating",
    kind: "custom",
    type: "text",
    label: "Puntuación UTR",
    required: false,
    enabled: true,
    show_in_players_table: true,
    help_text: "Solo para categoría A o A-B Femenil.",
    order: 450,
  },
  {
    key: "mex_amtp_ranking",
    kind: "custom",
    type: "text",
    label: "Ranking AMTP",
    required: false,
    enabled: true,
    show_in_players_table: true,
    help_text: "Solo para categoría A o A-B Femenil, si cuentas con ranking.",
    order: 460,
  },
  {
    key: "mex_women_fallback_category",
    kind: "custom",
    type: "select",
    label: "Si no se abre tu categoría femenil, ¿en cuál aceptarías jugar?",
    required: false,
    enabled: true,
    show_in_players_table: true,
    options: ["A-B Femenil", "Femenil C", "Femenil D", "Categoría mixta equivalente", "Prefiero no jugar si no se abre mi categoría"],
    help_text: "Opcional; aplica para jugadoras de categorías femeniles.",
    order: 470,
  },
  {
    key: "mex_whatsapp_group_joined",
    kind: "custom",
    type: "checkbox",
    label: "Confirmo que ya entré al grupo de WhatsApp del torneo.",
    required: true,
    enabled: true,
    show_in_players_table: false,
    order: 480,
  },
  {
    key: "mex_instagram_handle",
    kind: "custom",
    type: "text",
    label: "Instagram para historias del torneo",
    required: false,
    enabled: true,
    show_in_players_table: true,
    help_text: "Opcional. Si quieres que Mex Tour Cup te etiquete en historias, escribe tu @.",
    order: 490,
  },
];

const MEX_TOUR_CUP_CORE_OVERRIDES: RegistrationFormField[] = [
  { key: "first_name", kind: "core", type: "text", label: "Nombre", required: true, enabled: true, locked: true, show_in_players_table: true, order: 10 },
  { key: "last_name", kind: "core", type: "text", label: "Apellido paterno", required: true, enabled: true, locked: true, show_in_players_table: true, order: 20 },
  { key: "maternal_last_name", kind: "core", type: "text", label: "Apellido materno", required: false, enabled: false, show_in_players_table: false, order: 30 },
  { key: "phone", kind: "core", type: "phone", label: "Celular", required: true, enabled: true, show_in_players_table: true, order: 40 },
  { key: "email", kind: "core", type: "email", label: "Correo", required: true, enabled: true, locked: true, show_in_players_table: true, order: 50 },
  { key: "birthdate", kind: "core", type: "date", label: "Fecha de nacimiento", required: false, enabled: false, show_in_players_table: false, order: 60 },
  { key: "gender", kind: "core", type: "select", label: "Género", required: true, enabled: true, show_in_players_table: true, options: ["male", "female"], order: 70 },
  { key: "nationality_code", kind: "core", type: "text", label: "Nacionalidad", required: false, enabled: false, show_in_players_table: false, order: 80 },
  { key: "state_province", kind: "core", type: "text", label: "Estado", required: false, enabled: false, show_in_players_table: false, order: 90 },
  { key: "city", kind: "core", type: "text", label: "Ciudad", required: false, enabled: false, show_in_players_table: false, order: 100 },
  { key: "club_name", kind: "core", type: "text", label: "¿En dónde entrenas?", required: true, enabled: true, show_in_players_table: true, order: 110 },
  { key: "national_federation_id", kind: "core", type: "text", label: "ID Federativo / FMT", required: false, enabled: false, show_in_players_table: false, order: 120 },
  { key: "wtn_tennis_id", kind: "core", type: "text", label: "WTN Tennis ID", required: false, enabled: false, show_in_players_table: false, order: 130 },
  { key: "dominant_hand", kind: "core", type: "select", label: "Mano dominante", required: false, enabled: false, show_in_players_table: false, options: ["right", "left"], order: 140 },
  { key: "backhand_type", kind: "core", type: "select", label: "Tipo de revés", required: false, enabled: false, show_in_players_table: false, options: ["one_hand", "two_hands"], order: 150 },
  { key: "guardian_name", kind: "core", type: "text", label: "Nombre del tutor", required: false, conditional_required: "minor", enabled: false, show_in_players_table: false, order: 160 },
  { key: "guardian_phone", kind: "core", type: "phone", label: "Teléfono del tutor", required: false, conditional_required: "minor", enabled: false, show_in_players_table: false, order: 170 },
  { key: "guardian_relationship", kind: "core", type: "text", label: "Parentesco del tutor", required: false, conditional_required: "minor", enabled: false, show_in_players_table: false, order: 180 },
  { key: "guardian_email", kind: "core", type: "email", label: "Email del tutor", required: false, conditional_required: "minor", enabled: false, show_in_players_table: false, order: 190 },
  { key: "coach_name", kind: "core", type: "text", label: "Nombre del profesor", required: false, enabled: false, show_in_players_table: false, order: 200 },
  { key: "coach_phone", kind: "core", type: "phone", label: "Teléfono del profesor", required: false, enabled: false, show_in_players_table: false, order: 210 },
  { key: "coach_email", kind: "core", type: "email", label: "Email del profesor", required: false, enabled: false, show_in_players_table: false, order: 220 },
  { key: "curp_pdf", kind: "document", type: "file", label: "CURP en PDF", required: false, enabled: false, show_in_players_table: false, order: 300 },
  { key: "photo", kind: "document", type: "file", label: "Foto de medio cuerpo", required: false, enabled: false, show_in_players_table: false, order: 310 },
  { key: "payment_receipt", kind: "document", type: "file", label: "Comprobante de pago", required: false, enabled: true, show_in_players_table: true, order: 320 },
];

export const MEX_TOUR_CUP_ORGANIZER_EMAILS = new Set(["art.gar.lion@gmail.com"]);

export function mexTourCupRegistrationFormSchema({ credential = false }: { credential?: boolean } = {}): RegistrationFormSchema {
  const schema = normalizeRegistrationFormSchema({
    version: 1,
    fields: [...MEX_TOUR_CUP_CORE_OVERRIDES, ...MEX_TOUR_CUP_CUSTOM_FIELDS],
  });
  return credential ? withCredentialPhoto(schema).schema : schema;
}

// ------------------------------------------------- credencial y foto
// Eduardo 2026-10-30: con credencial, la «Foto de medio cuerpo» es Activa y
// Obligatoria (la credencial lleva la foto). El servidor rechaza con 409
// `credential_requires_photo` un formulario que no la pida; las plantillas
// la fuerzan y el mensaje de éxito lo dice.
export const CREDENTIAL_PHOTO_KEY = "photo";
/** Documentos que el editor muestra arriba (bloque «Documentos»). Solo es la
 * vista del editor: `order` no cambia, así que el link público y las listas
 * de pendientes siguen igual. */
export const EDITOR_DOCUMENT_KEYS: readonly string[] = ["curp_pdf", "photo"];

/** ¿El formulario pide la foto como Activa y Obligatoria? */
export function credentialPhotoOk(fields: RegistrationFormField[]): boolean {
  const photo = fields.find((field) => field.key === CREDENTIAL_PHOTO_KEY);
  return Boolean(photo && photo.enabled !== false && photo.required);
}

/** Deja la foto Activa y Obligatoria; `forced` dice si cambió algo. */
export function withCredentialPhoto(schema: RegistrationFormSchema): { schema: RegistrationFormSchema; forced: boolean } {
  const forced = !credentialPhotoOk(schema.fields);
  return {
    forced,
    schema: {
      ...schema,
      fields: schema.fields.map((field) =>
        field.key === CREDENTIAL_PHOTO_KEY ? { ...field, enabled: true, required: true } : field,
      ),
    },
  };
}

/** Plantilla MÍNIMA: solo lo que el sistema exige para dar de alta a un
 * jugador (nombre, apellido paterno y correo). Todo lo demás queda apagado.
 *
 * Pedido de Eduardo (2026-08-06): el default trae 11 campos activos y 9
 * obligatorios, así que un torneo que solo quiere una lista rápida obliga al
 * jugador a llenar de más. Con esta plantilla el admin arranca del piso y
 * enciende lo que necesite, en vez de apagar de uno en uno.
 */
export function minimalRegistrationFormSchema({ kids = false, credential = false }: { kids?: boolean; credential?: boolean } = {}): RegistrationFormSchema {
  const schema = normalizeRegistrationFormSchema({
    version: 1,
    fields: DEFAULT_REGISTRATION_FORM_FIELDS.map((field) => {
      const esDelSistema = SYSTEM_REQUIRED_REGISTRATION_FIELD_KEYS.has(field.key);
      // Con categorías infantiles (Eduardo 2026-10-01) también van los datos
      // del papá, mamá o tutor: con ellos se arma la Cuenta Familiar.
      if (kids && MINIMAL_KIDS_GUARDIAN_KEYS.has(field.key)) {
        return {
          ...field,
          enabled: true,
          required: field.key !== "guardian_relationship",
          conditional_required: "minor",
          show_in_players_table: false,
        };
      }
      return {
        ...field,
        enabled: esDelSistema,
        required: esDelSistema,
        // La tabla de registros muestra solo lo mínimo para identificar.
        show_in_players_table: esDelSistema,
      };
    }),
  });
  // Con credencial la foto va Activa y Obligatoria (Eduardo 2026-10-30).
  return credential ? withCredentialPhoto(schema).schema : schema;
}

/** Datos del tutor que enciende la plantilla mínima con categorías infantiles
 * (nombre, celular y correo obligatorios para menores; parentesco opcional). */
const MINIMAL_KIDS_GUARDIAN_KEYS = new Set(["guardian_name", "guardian_phone", "guardian_email", "guardian_relationship"]);
/** Los que deben estar encendidos para contar como la variante infantil
 * (los datos mínimos del tutor, `GUARDIAN_MINIMUM_KEYS`). */
const MINIMAL_KIDS_REQUIRED_KEYS = ["guardian_name", "guardian_phone", "guardian_email"];

/** true cuando el formulario ya está en el mínimo del sistema, en cualquiera
 * de sus dos variantes: sin tutor, o con los datos del tutor (infantiles).
 * Con `kids` (el torneo tiene categorías infantiles) solo cuenta la variante
 * con tutor: sin su correo no hay Cuenta Familiar y el botón debe ofrecerla. */
export function isMinimalRegistrationSchema(schema?: RegistrationFormSchema | null, options: { kids?: boolean; credential?: boolean } = {}) {
  const fields = normalizeRegistrationFormSchema(schema).fields;
  const isEnabled = (key: string) => fields.some((field) => field.key === key && field.enabled !== false);
  const kidsVariant = MINIMAL_KIDS_REQUIRED_KEYS.every(isEnabled);
  if (options.kids && !kidsVariant) return false;
  return fields.every((field) => {
    if (SYSTEM_REQUIRED_REGISTRATION_FIELD_KEYS.has(field.key)) return field.enabled !== false;
    if (kidsVariant && MINIMAL_KIDS_GUARDIAN_KEYS.has(field.key)) return true;
    // Con credencial, «mínimos» lleva la foto Activa y Obligatoria.
    if (options.credential && field.key === CREDENTIAL_PHOTO_KEY) return field.enabled !== false && Boolean(field.required);
    return field.enabled === false;
  });
}

// ---------------------------------------------------------------- secciones
// «Jugador» / «Papá, mamá o tutor» / «Otros» (Eduardo 2026-10-01): dónde ve
// la familia cada campo en el formulario público. Sin migración: la sección
// viaja en el JSON del formulario y solo cuenta en campos personalizados.

/** Campos del sistema que van con el jugador (sección fija). */
export const PLAYER_SECTION_KEYS: ReadonlySet<string> = new Set([
  "first_name", "last_name", "maternal_last_name", "phone", "email", "birthdate", "gender",
  "nationality_code", "state_province", "city", "club_name", "national_federation_id",
  "wtn_tennis_id", "dominant_hand", "backhand_type", "curp_pdf", "photo", "partner_name",
  "partner_contact",
]);
/** Datos del papá, mamá o tutor (sección fija). */
export const GUARDIAN_SECTION_KEYS: ReadonlySet<string> = new Set([
  "guardian_name", "guardian_phone", "guardian_relationship", "guardian_email",
]);
/** Profesor y comprobante (sección fija). */
export const OTHER_SECTION_KEYS: ReadonlySet<string> = new Set([
  "coach_name", "coach_phone", "coach_email", "payment_receipt",
]);

export const REGISTRATION_FIELD_SECTIONS: readonly RegistrationFieldSection[] = ["player", "guardian", "other"];

export const REGISTRATION_FIELD_SECTION_LABELS: Record<RegistrationFieldSection, string> = {
  player: "Jugador",
  guardian: "Papá, mamá o tutor",
  other: "Otros",
};

function isFieldSection(value: unknown): value is RegistrationFieldSection {
  return value === "player" || value === "guardian" || value === "other";
}

function hasFixedSection(key: string): boolean {
  return PLAYER_SECTION_KEYS.has(key) || GUARDIAN_SECTION_KEYS.has(key) || OTHER_SECTION_KEYS.has(key);
}

/** Sección donde la familia ve el campo. Los del sistema la tienen fija; un
 * campo personalizado usa la guardada o «Otros». Espejo de `field_section`
 * del backend: nunca adivina por la etiqueta (eso solo lo sugiere el editor). */
export function fieldSection(field: Pick<RegistrationFormField, "key" | "section">): RegistrationFieldSection {
  if (PLAYER_SECTION_KEYS.has(field.key)) return "player";
  if (GUARDIAN_SECTION_KEYS.has(field.key)) return "guardian";
  if (OTHER_SECTION_KEYS.has(field.key)) return "other";
  return isFieldSection(field.section) ? field.section : "other";
}

const GUARDIAN_LABEL_PATTERN = /tutor|pap[aá]|mam[aá]|padre|madre/i;

/** Campo personalizado sin sección cuya etiqueta habla del tutor: el editor
 * sugiere moverlo a «Papá, mamá o tutor». Solo el clic del organizador lo
 * cambia; los torneos ya publicados no cambian solos. */
export function suggestsGuardianSection(field: Pick<RegistrationFormField, "key" | "kind" | "label" | "section">): boolean {
  if (field.kind !== "custom" || hasFixedSection(field.key)) return false;
  if (isFieldSection(field.section)) return false;
  return GUARDIAN_LABEL_PATTERN.test(field.label ?? "");
}

export function isMexTourCupSchema(schema?: RegistrationFormSchema | null) {
  const fields = normalizeRegistrationFormSchema(schema).fields;
  return MEX_TOUR_CUP_CUSTOM_FIELDS.every((presetField) =>
    fields.some((field) => field.key === presetField.key && field.enabled !== false),
  );
}

export function normalizeRegistrationFormSchema(schema?: RegistrationFormSchema | null): RegistrationFormSchema {
  const byKey = new Map(DEFAULT_REGISTRATION_FORM_FIELDS.map((field) => [field.key, { ...field }]));
  (schema?.fields ?? []).forEach((field, index) => {
    if (!field?.key) return;
    const base = byKey.get(field.key) ?? {} as RegistrationFormField;
    const locked = Boolean(field.locked || base.locked || SYSTEM_REQUIRED_REGISTRATION_FIELD_KEYS.has(field.key));
    byKey.set(field.key, {
      ...base,
      ...field,
      locked,
      enabled: locked ? true : field.enabled !== false,
      required: locked ? true : Boolean(field.required),
      order: Number(field.order ?? base.order ?? (index + 1) * 10),
    });
  });
  return { version: 1, fields: Array.from(byKey.values()).sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0)) };
}

export function enabledRegistrationFields(schema?: RegistrationFormSchema | null) {
  return normalizeRegistrationFormSchema(schema).fields.filter((field) => field.enabled !== false);
}

export function tableRegistrationFields(schema?: RegistrationFormSchema | null) {
  return enabledRegistrationFields(schema).filter((field) => Boolean(field.show_in_players_table || field.visible_in_players_table));
}

export function registrationFieldValue(participant: TournamentParticipant | null | undefined, field: RegistrationFormField): string {
  if (!participant) return "—";
  if (field.kind === "document") {
    const docs = participant.registration_documents;
    const kind = field.key === "photo" || field.key === "curp_pdf" || field.key === "payment_receipt" ? field.key : null;
    return kind && registrationDocumentReceived(docs, kind) ? "Cargado" : "Pendiente";
  }
  if (field.kind === "custom") {
    const value = participant.registration_custom_answers?.[field.key];
    if (Array.isArray(value)) return value.join(", ");
    return value === null || value === undefined || value === "" ? "—" : String(value);
  }
  const player = participant.player as unknown as Record<string, unknown> | null | undefined;
  const value = player?.[field.key];
  if (field.key === "gender") return value === "male" ? "Masculino" : value === "female" ? "Femenino" : "—";
  if (field.key === "dominant_hand") return value === "right" ? "Derecha" : value === "left" ? "Izquierda" : "—";
  if (field.key === "backhand_type") return value === "one_hand" ? "Una mano" : value === "two_hands" ? "Dos manos" : "—";
  return value === null || value === undefined || value === "" ? "—" : String(value);
}

export function isRegistrationFieldRequired(
  field: RegistrationFormField,
  form: Pick<PublicRegistrationSubmitInput, "birthdate">,
  isMinor: boolean,
  whoPlays: PublicWhoPlays = "self",
) {
  void form;
  if (field.enabled === false) return false;
  // Regla de producto: el comprobante de pago NUNCA es campo obligatorio del
  // FORMULARIO — se sube en la etapa de pago. Su uploader vive en el paso 4
  // (Pago), así que exigirlo aquí (validación del paso 3) brickeaba el link:
  // "Antes de pasar a pago falta completar: Comprobante de pago" sin forma de
  // llegar al paso donde se sube. El uploader del paso de pago sigue siendo
  // opcional ("Guardar y subir comprobante después").
  if (field.key === "payment_receipt") return false;
  // Un campo personalizado del tutor solo se pide si inscribe el papá o la
  // mamá («Mi hijo o hija») o si el jugador es menor.
  if (field.kind === "custom" && fieldSection(field) === "guardian") {
    return Boolean(field.required && (whoPlays === "child" || isMinor));
  }
  // En modo hijo el tutor es el dueño de la cuenta: se pide a cualquier edad.
  if (whoPlays === "child" && GUARDIAN_SECTION_KEYS.has(field.key)) return Boolean(field.required);
  // En «Yo» los datos del tutor del sistema son SOLO de menores, aunque el
  // formulario guardado haya perdido «conditional_required» (un adulto no
  // tiene tutor; su caja ni se ve). Igual que el servidor.
  if (field.conditional_required === "minor" || (field.kind === "core" && GUARDIAN_SECTION_KEYS.has(field.key))) {
    return Boolean(field.required && isMinor);
  }
  return Boolean(field.required);
}

/** Datos mínimos del papá, mamá o tutor: nombre y contacto. Son los que el
 * sistema prende solo con la primera categoría de menores (Eduardo
 * 2026-10-03) y los que pide «Aplicar campos mínimos» con menores. */
export const GUARDIAN_MINIMUM_KEYS: readonly string[] = ["guardian_name", "guardian_phone", "guardian_email"];

/** Aviso fijo cuando el sistema los prendió (categorías y formulario). */
export const GUARDIAN_AUTO_ENABLED_NOTICE =
  "Prendimos los datos del papá, mamá o tutor porque este torneo tiene categorías de menores.";

/** El sistema los prendió, pero ya no queda ninguna categoría de menores
 * (se renombró, se desactivó o se borró). Nunca se apagan solos: se explica. */
export const GUARDIAN_STILL_ON_HINT =
  "Los datos del papá, mamá o tutor siguen prendidos, pero este torneo ya no tiene categorías de menores. Si no los necesitas, apágalos desde «Editar campos», en «Formulario y link público».";

/** Los datos mínimos del tutor que están apagados en el formulario. */
export function guardianMinimumFieldsOff(fields: RegistrationFormField[]): RegistrationFormField[] {
  const normalized = normalizeRegistrationFormSchema({ version: 1, fields }).fields;
  return GUARDIAN_MINIMUM_KEYS.map((key) => normalized.find((field) => field.key === key)).filter(
    (field): field is RegistrationFormField => Boolean(field && field.enabled === false),
  );
}

/** «Email del tutor» (la etiqueta de siempre) se dice «Correo del tutor»; una
 * etiqueta que el organizador cambió se respeta. */
function guardianWarningLabel(field: RegistrationFormField): string {
  const label = String(field.label ?? "").trim();
  return /^email del tutor$/i.test(label) ? "Correo del tutor" : label;
}

/** «a», «a y b», «a, b y c». */
function joinWithY(items: string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} y ${items[items.length - 1]}`;
}

/** Advertencia en línea (sin diálogo) cuando el torneo tiene categorías de
 * menores y hay datos mínimos del tutor apagados (`guardianMinimumFieldsOff`).
 * Solo dice que no habrá cómo contactar a la familia si están apagados el
 * celular Y el correo. "" si no hay nada apagado. */
export function guardianOffWarningText(off: RegistrationFormField[]): string {
  if (off.length === 0) return "";
  const one = off.length === 1;
  const names = joinWithY(off.map((field) => `«${guardianWarningLabel(field)}»`));
  const keys = new Set(off.map((field) => field.key));
  const noContact = keys.has("guardian_phone") && keys.has("guardian_email");
  const consequence = noContact
    ? "sin esos datos no tendrás cómo contactar al papá, mamá o tutor de cada menor."
    : one
      ? "ese dato no se le pedirá a la familia de cada menor."
      : "esos datos no se le pedirán a la familia de cada menor.";
  return `Ojo: este torneo tiene categorías de menores y ${one ? "está apagado" : "están apagados"} ${names}. Puedes ${one ? "dejarlo" : "dejarlos"} así, pero ${consequence}`;
}
